import { ErrorModal, HelmetDexteel, axiosInstance } from "@dexteel/mesf-core";
import {
  Box,
  Button,
  Collapse,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  Error as ErrorIcon,
  GetApp as GetAppIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useEffect, useState } from "react";

interface ProcessGradeStatus {
  progressPercent: number;
  currentStatus: string;
  isComplete: boolean;
  result?: {
    success: boolean;
    message: string;
    processedGrades: Array<{
      gradeId: number;
      gradeName: string;
      comments: string;
      elements: Array<{
        elementId: number;
        elementName: string;
        maxValue: string;
        minValue: string;
        isNew: boolean;
        importStatus: string;
        importMessage: string;
      }>;
      deletedElements: Array<{
        elementId: number;
        elementName: string;
        maxValue: string;
        minValue: string;
      }>;
      isExistingGrade: boolean;
      chemistryLimitsDeleted: boolean;
      importStatus: string;
      importMessage: string;
      totalElements: number;
      importedElements: number;
    }>;
    importLog: string[];
  };
}

type Props = {};

const JOB_ID_STORAGE_KEY = "uploadGradesJobId";

export const UploadGradesPage = (props: Props) => {
  const theme = useTheme();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");
  const [uploadResult, setUploadResult] = useState<{
    fileId: string;
    success: boolean;
    message: string;
  } | null>(null);
  const [processingStatus, setProcessingStatus] =
    useState<ProcessGradeStatus | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [logDownloaded, setLogDownloaded] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const uploadPath = "/configuration/steel-grades/upload";
  const [showInfoAlert, setShowInfoAlert] = useState(false);
  const [showFormatInfo, setShowFormatInfo] = useState(false);

  const isProcessing =
    isUploading ||
    (!!processingStatus && !processingStatus.isComplete) ||
    isRestoring;

  const saveJobIdToStorage = (jobId: string) => {
    try {
      localStorage.setItem(JOB_ID_STORAGE_KEY, jobId);
    } catch (error) {
      console.error("Error saving jobId to localStorage:", error);
    }
  };

  const getJobIdFromStorage = (): string | null => {
    try {
      return localStorage.getItem(JOB_ID_STORAGE_KEY);
    } catch (error) {
      console.error("Error getting jobId from localStorage:", error);
      return null;
    }
  };

  const clearJobIdFromStorage = () => {
    try {
      localStorage.removeItem(JOB_ID_STORAGE_KEY);
    } catch (error) {
      console.error("Error clearing jobId from localStorage:", error);
    }
  };

  useEffect(() => {
    const savedJobId = getJobIdFromStorage();
    if (savedJobId && !jobId) {
      setIsRestoring(true);
      setJobId(savedJobId);
    }
  }, []);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isProcessing) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isProcessing]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isProcessing) return;
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
      setUploadResult(null);
      setProcessingStatus(null);
      setJobId(null);
      setShowResults(false);
      setError("");
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (isProcessing) return;
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (isProcessing) return;
    event.preventDefault();
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      setSelectedFile(event.dataTransfer.files[0]);
      setUploadResult(null);
      setProcessingStatus(null);
      setJobId(null);
      setShowResults(false);
      setError("");
    }
  };

  const uploadFile = async () => {
    if (!selectedFile) {
      setError("Please select a file to upload.");
      return;
    }

    const allowedExtensions = [".csv"];
    const fileExtension =
      "." + selectedFile.name.split(".").pop()?.toLowerCase();

    if (!allowedExtensions.includes(fileExtension as string)) {
      setError(`File type not supported. Please upload a CSV file.`);
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await axiosInstance.post("/api/grades", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const result = response.data;

      if (response.status === 200) {
        setUploadResult({
          fileId: result.fileId,
          success: true,
          message: "File uploaded successfully",
        });
        await startProcessing(result.fileId);
      } else {
        setError(result.message || "Failed to upload file");
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message || "An error occurred during file upload",
      );
      console.error(err);
    } finally {
      setIsUploading(false);
    }
  };

  const startProcessing = async (fileId: string) => {
    try {
      const response = await axiosInstance.post("/api/grades/process-file", {
        fileId,
        processAllRows: true,
      });

      const result = response.data;

      if (response.status === 200) {
        setJobId(result.jobId);
        saveJobIdToStorage(result.jobId);
        setProcessingStatus({
          progressPercent: 0,
          currentStatus: "Starting processing...",
          isComplete: false,
        });
        setShowInfoAlert(true);
      } else {
        setError(result.message || "Failed to start processing");
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "An error occurred while starting the processing",
      );
      console.error(err);
    }
  };

  useEffect(() => {
    if (!jobId) return;

    let interval: ReturnType<typeof setInterval> | null = null;
    let stopped = false;

    const checkStatus = async () => {
      try {
        const response = await axiosInstance.get(
          `/api/grades/processing-status/${jobId}`,
        );
        const status: ProcessGradeStatus = response.data;
        setProcessingStatus(status);
        if (status.isComplete && !stopped) {
          setShowResults(true);
          stopped = true;
          if (interval) clearInterval(interval);
          if (status.result && !status.result.success) {
            setError(status.result.message);
          }
        }
      } catch (err: any) {
        console.error("Error checking processing status:", err);
        if (!stopped) {
          if (err.response?.status === 404) {
            setError("El proceso ya no existe o ha finalizado.");
            setShowInfoAlert(false);
            setProcessingStatus(null);
            setJobId(null);
          } else {
            setError(
              "Error checking processing status. The job might have been completed or cancelled.",
            );
          }
          stopped = true;
          if (interval) clearInterval(interval);
        }
      }
    };

    if (isRestoring) {
      checkStatus();
      setIsRestoring(false);
    }

    interval = setInterval(checkStatus, 1000);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [jobId, isRestoring]);

  const downloadImportLog = async () => {
    if (!jobId) return;
    try {
      const response = await axiosInstance.get(
        `/api/grades/import-log/${jobId}`,
        {
          responseType: "blob",
        },
      );

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `import_log_${jobId}.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setLogDownloaded(true);
      await axiosInstance.delete(`/api/grades/job/${jobId}`);
      clearJobIdFromStorage();
      setTimeout(() => resetForm(), 500);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "An error occurred while downloading the import log",
      );
      console.error(err);
    }
  };

  const resetForm = async () => {
    if (jobId) {
      try {
        await axiosInstance.delete(`/api/grades/job/${jobId}`);
      } catch (err) {
        console.error("Error deleting job:", err);
      }
    }
    setSelectedFile(null);
    setUploadResult(null);
    setProcessingStatus(null);
    setJobId(null);
    setShowResults(false);
    setError("");
    setShowLog(false);
    setLogDownloaded(false);
    setIsRestoring(false);
    clearJobIdFromStorage();
    const fileInput = document.getElementById("file-input") as HTMLInputElement;
    if (fileInput) fileInput.value = "";
  };

  useEffect(() => {
    if (showResults) setShowInfoAlert(false);
  }, [showResults]);

  const handleCancelProcessing = async () => {
    if (jobId) {
      try {
        const response = await axiosInstance.delete(`/api/grades/job/${jobId}`);
        if (response.status === 200) {
          setProcessingStatus(null);
          setJobId(null);
          setShowResults(false);
          setShowInfoAlert(false);
          setError("The import process was cancelled by the user.");
          setSelectedFile(null);
          setUploadResult(null);
        } else {
          setError("Failed to cancel import process.");
        }
      } catch (err: any) {
        setError(
          err.response?.data?.message || "Failed to cancel import process.",
        );
      }
    }
  };

  const downloadDemoFile = async () => {
    try {
      const response = await axiosInstance.get("/api/grades/download-demo", {
        responseType: "blob",
      });

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "GradesDemo.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "An error occurred while downloading the demo file",
      );
      console.error(err);
    }
  };

  const renderResults = () => {
    if (!processingStatus?.result) return null;
    const { result } = processingStatus;
    return (
      <Box sx={{ marginTop: 3 }}>
        <Box
          style={{
            marginBottom: 16,
            padding: 16,
            backgroundColor: result.success ? "#e8f5e8" : "#ffebee",
            border: `1px solid ${result.success ? "#4caf50" : "#f44336"}`,
            borderRadius: 4,
            color: result.success ? "#2e7d32" : "#c62828",
          }}
        >
          <Typography
            variant="h6"
            style={{ display: "flex", alignItems: "center" }}
          >
            {result.success ? (
              <CheckCircleIcon style={{ marginRight: 8 }} />
            ) : (
              <ErrorIcon style={{ marginRight: 8 }} />
            )}
            {result.success ? "Imported successfully" : "Import Failed"}
          </Typography>
          <Typography>{result.message}</Typography>
        </Box>
        {result.success && (
          <>
            <Button
              variant="contained"
              color="primary"
              onClick={resetForm}
              style={{ marginTop: 16, marginRight: 8 }}
            >
              Upload Another File
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setShowLog((prev) => !prev)}
              style={{ marginTop: 16 }}
            >
              {showLog ? "Hide Log" : "View Log"}
            </Button>
            {showLog && (
              <>
                <Box
                  sx={{
                    maxHeight: "300px",
                    overflow: "auto",
                    backgroundColor: (theme) => theme.palette.grey[50],
                    padding: 1,
                    borderRadius: 0.5,
                    fontFamily: "monospace",
                    fontSize: "0.75rem",
                    marginTop: 2,
                  }}
                >
                  <pre
                    style={{ margin: 0, fontFamily: "monospace", fontSize: 13 }}
                  >
                    {result.importLog && result.importLog.length > 0
                      ? result.importLog.join("\n")
                      : "No log available."}
                  </pre>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={downloadImportLog}
                  style={{ marginTop: 16 }}
                  disabled={logDownloaded}
                >
                  Download Import Log
                </Button>
              </>
            )}
          </>
        )}
        {logDownloaded && (
          <Typography variant="body2" style={{ color: "green", marginTop: 16 }}>
            Log downloaded. You can upload another file.
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <>
      <HelmetDexteel title={"Upload Grades"} />
      <Grid container justifyContent="center" alignItems="center">
        <Grid item md={12} xs={12} style={{ margin: 0 }}>
          <Paper elevation={1}>
            <Typography variant="h5" gutterBottom>
              Upload Grades
            </Typography>
            <Typography variant="body2" gutterBottom>
              Upload a CSV file to update grades. Excel files (.xls, .xlsx) are
              not yet supported.
            </Typography>

            <Box
              style={{ marginTop: 16, marginBottom: showFormatInfo ? 0 : 16 }}
            >
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setShowFormatInfo(!showFormatInfo)}
                style={{ marginRight: showFormatInfo ? 8 : 0 }}
              >
                {showFormatInfo ? "Hide Format Info" : "Show Expected Format"}
              </Button>
              {showFormatInfo && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<GetAppIcon />}
                  onClick={downloadDemoFile}
                >
                  Download Demo File
                </Button>
              )}
            </Box>

            <Collapse in={showFormatInfo}>
              <Box
                sx={{
                  marginTop: 3,
                  marginBottom: 4,
                  padding: 2,
                  border: (theme) => `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: 1,
                  backgroundColor: (theme) => theme.palette.grey[50],
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Expected Format
                </Typography>
                <Typography variant="body2" paragraph>
                  The file must contain at least the first two columns as shown
                  below. The following columns can be any elements needed, using
                  the format <b>Element_Max</b>, <b>Element_Min</b>. Each row
                  represents a grade with its chemical composition limits.
                </Typography>

                <TableContainer
                  component={Paper}
                  sx={{
                    marginTop: 2,
                    "& th": {
                      backgroundColor: "primary.main",
                      color: "primary.contrastText",
                      fontWeight: "bold",
                    },
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Column Name</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Example</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <strong>GradeName</strong>
                        </TableCell>
                        <TableCell>Unique identifier for the grade</TableCell>
                        <TableCell>1018</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Comments</strong>
                        </TableCell>
                        <TableCell>Optional description or notes</TableCell>
                        <TableCell>Standard carbon steel grade</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Al_Max</strong>
                        </TableCell>
                        <TableCell>Maximum Aluminum content (%)</TableCell>
                        <TableCell>0.01</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Al_Min</strong>
                        </TableCell>
                        <TableCell>Minimum Aluminum content (%)</TableCell>
                        <TableCell>0</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>C_Max</strong>
                        </TableCell>
                        <TableCell>Maximum Carbon content (%)</TableCell>
                        <TableCell>0.2</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>C_Min</strong>
                        </TableCell>
                        <TableCell>Minimum Carbon content (%)</TableCell>
                        <TableCell>0.15</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Mn_Max</strong>
                        </TableCell>
                        <TableCell>Maximum Manganese content (%)</TableCell>
                        <TableCell>0.9</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Mn_Min</strong>
                        </TableCell>
                        <TableCell>Minimum Manganese content (%)</TableCell>
                        <TableCell>0.6</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Si_Max</strong>
                        </TableCell>
                        <TableCell>Maximum Silicon content (%)</TableCell>
                        <TableCell>0.4</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Si_Min</strong>
                        </TableCell>
                        <TableCell>Minimum Silicon content (%)</TableCell>
                        <TableCell>0.15</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography
                            variant="body2"
                            style={{ fontStyle: "italic" }}
                          >
                            <strong>Note:</strong> The file can include more
                            chemical elements (Cr, Ni, Mo, P, S, etc.) than the
                            ones shown above. Download the demo file for a
                            reference structure and add/delete columns or rows
                            as needed.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Collapse>

            {isRestoring && (
              <Box
                style={{
                  marginBottom: 16,
                  padding: 16,
                  backgroundColor: "#fff3cd",
                  border: "1px solid #ffeaa7",
                  borderRadius: 4,
                  color: "#856404",
                }}
              >
                <Typography variant="body1">
                  <WarningIcon
                    style={{ marginRight: 8, verticalAlign: "middle" }}
                  />
                  Reconnecting with last process...
                </Typography>
              </Box>
            )}

            <Collapse in={!showResults}>
              <Box
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() =>
                  !isProcessing &&
                  document.getElementById("file-input")?.click()
                }
                sx={{
                  border: (theme) => `2px dashed ${theme.palette.grey[400]}`,
                  borderRadius: 1,
                  padding: 3,
                  textAlign: "center",
                  cursor: "pointer",
                  pointerEvents: isProcessing ? "none" : "auto",
                  opacity: isProcessing ? 0.5 : 1,
                  "&:hover": {
                    backgroundColor: (theme) => theme.palette.grey[100],
                    borderColor: "primary.main",
                  },
                }}
              >
                <CloudUploadIcon fontSize="large" color="primary" />
                <Typography variant="body1">
                  Drag and drop a CSV file here, or click to select a file
                </Typography>
                {selectedFile && (
                  <Typography variant="body2" sx={{ marginTop: 2 }}>
                    Selected file: {selectedFile.name}
                  </Typography>
                )}
              </Box>

              <input
                id="file-input"
                type="file"
                accept=".csv"
                style={{ display: "none" }}
                onChange={handleFileChange}
                disabled={!!isProcessing}
              />

              <Button
                variant="contained"
                color="primary"
                sx={{ marginTop: 2, marginRight: 2 }}
                startIcon={<CloudUploadIcon />}
                onClick={uploadFile}
                disabled={!selectedFile || isUploading || isProcessing}
              >
                {isUploading ? "Uploading..." : "Upload & Process File"}
              </Button>

              {uploadResult && uploadResult.success && (
                <Typography
                  variant="body1"
                  style={{ marginTop: 16, color: "green" }}
                >
                  File uploaded successfully!
                </Typography>
              )}
            </Collapse>

            {processingStatus && !showResults && (
              <Box
                sx={{
                  marginTop: 3,
                  padding: 2,
                  border: (theme) => `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: 1,
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Processing File...
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={
                    typeof processingStatus.progressPercent === "number" &&
                    !isNaN(processingStatus.progressPercent)
                      ? processingStatus.progressPercent
                      : 0
                  }
                  sx={{ marginBottom: 1 }}
                />
                <Typography sx={{ marginBottom: 2, fontWeight: 500 }}>
                  {processingStatus.currentStatus}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Progress:{" "}
                  {typeof processingStatus.progressPercent === "number" &&
                  !isNaN(processingStatus.progressPercent)
                    ? processingStatus.progressPercent
                    : 0}
                  %
                </Typography>
                <div style={{ marginTop: 10 }}>
                  <div style={{ marginBottom: 4 }}>
                    <span
                      style={{
                        color: "textSecondary",
                        fontSize: 14,
                        fontWeight: 500,
                      }}
                    >
                      You can navigate to other pages.
                    </span>
                    <span
                      style={{
                        color: theme.palette.text.secondary,
                        fontSize: 14,
                        marginLeft: 4,
                      }}
                    >
                      The import process will continue in the background.
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      onClick={handleCancelProcessing}
                      style={{ minWidth: 140 }}
                    >
                      Cancel Import
                    </Button>
                  </div>
                </div>
              </Box>
            )}

            {showResults && renderResults()}
          </Paper>
        </Grid>
      </Grid>
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
